import React from "react";
import { formatDate } from "@/utils/timezone";
import { formatCurrency, decodeHtmlEntities } from "@/utils/formatters";
import BaseModal from "./BaseModal";

const TradeSignalModal = ({ signal, onClose, onUpdateStatus }) => {
  if (!signal) return null;

  const getStatusBadgeColor = (status) => {
    const colors = {
      proposed:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      accepted:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      executed:
        "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
      dismissed:
        "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      expired: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getSignalTypeBadgeColor = (type) => {
    return type === "BUY"
      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
      : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  };

  const canAccept = signal.status === "proposed";
  const canExecute = signal.status === "accepted";
  const canDismiss = ["proposed", "accepted"].includes(signal.status);

  const header = (
    <div className="flex items-center justify-between p-6">
      <div className="flex items-center gap-3">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {signal.ticker}
        </h2>
        <span
          className={`px-3 py-1 rounded-full text-sm font-semibold ${getSignalTypeBadgeColor(
            signal.signal_type,
          )}`}
        >
          {signal.signal_type}
        </span>
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
            signal.status,
          )}`}
        >
          {signal.status.charAt(0).toUpperCase() + signal.status.slice(1)}
        </span>
      </div>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end gap-3 p-6">
      {canDismiss && (
        <button
          onClick={() => onUpdateStatus(signal.id, "dismissed")}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Dismiss
        </button>
      )}
      {canAccept && (
        <button
          onClick={() => onUpdateStatus(signal.id, "accepted")}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          Accept Signal
        </button>
      )}
      {canExecute && (
        <button
          onClick={() => onUpdateStatus(signal.id, "executed")}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
        >
          Mark Executed
        </button>
      )}
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
      >
        Close
      </button>
    </div>
  );

  return (
    <BaseModal header={header} footer={footer} onClose={onClose}>
      <div className="p-6 space-y-6">
        {/* Price Change Since Post */}
        {signal.price_change_since_post &&
          signal.intel &&
          signal.intel.length > 0 &&
          (() => {
            // Find the earliest captured_at date
            const earliestIntel = signal.intel.reduce((earliest, intel) =>
              new Date(intel.captured_at) < new Date(earliest.captured_at)
                ? intel
                : earliest,
            );

            return (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Price Change Since Post (
                      {formatDate(earliestIntel.captured_at)})
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(
                          signal.price_change_since_post.current_price,
                        )}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        from{" "}
                        {formatCurrency(
                          signal.price_change_since_post.price_at_date,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`text-2xl font-bold ${
                        signal.price_change_since_post.change_percent >= 0
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {signal.price_change_since_post.change_percent >= 0
                        ? "+"
                        : ""}
                      {signal.price_change_since_post.change_percent.toFixed(2)}
                      %
                    </div>
                    <div
                      className={`text-sm ${
                        signal.price_change_since_post.change_amount >= 0
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {signal.price_change_since_post.change_amount >= 0
                        ? "+"
                        : ""}
                      {formatCurrency(
                        Math.abs(signal.price_change_since_post.change_amount),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

        {/* Price Targets */}
        {(signal.suggested_price ||
          signal.entry_target ||
          signal.profit_target ||
          signal.stop_loss) && (
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Price Targets
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {signal.suggested_price && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Suggested Price
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrency(signal.suggested_price)}
                  </p>
                </div>
              )}
              {signal.entry_target && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Entry Target
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrency(signal.entry_target)}
                  </p>
                </div>
              )}
              {signal.profit_target && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Profit Target
                  </p>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(signal.profit_target)}
                  </p>
                </div>
              )}
              {signal.stop_loss && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Stop Loss
                  </p>
                  <p className="text-lg font-bold text-red-600 dark:text-red-400">
                    {formatCurrency(signal.stop_loss)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rationale */}
        {signal.rationale && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Rationale
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
              {decodeHtmlEntities(signal.rationale)}
            </p>
          </div>
        )}

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Source</p>
            <p className="font-medium text-gray-900 dark:text-white capitalize">
              {signal.source_type}
            </p>
          </div>
          {signal.confidence_score && (
            <div>
              <p className="text-gray-500 dark:text-gray-400">Confidence</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {signal.confidence_score}%
              </p>
            </div>
          )}
          <div>
            <p className="text-gray-500 dark:text-gray-400">Created</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {formatDate(signal.created_at)}
            </p>
          </div>
          {signal.expires_at && (
            <div>
              <p className="text-gray-500 dark:text-gray-400">Expires</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {formatDate(signal.expires_at)}
              </p>
            </div>
          )}
        </div>

        {/* Linked Intel */}
        {signal.intel && signal.intel.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Linked Intelligence ({signal.intel.length})
            </h3>
            <div className="space-y-2">
              {signal.intel.map((intel) => (
                <div
                  key={intel.id}
                  className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(intel.captured_at)} •{" "}
                        {intel.platform || intel.source_type}
                      </p>
                      {intel.thesis && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                          {decodeHtmlEntities(intel.thesis)}
                        </p>
                      )}
                      {intel.author && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          by {intel.author}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default TradeSignalModal;
